import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Checkbox, Typography, Button, Input, Modal, Tooltip, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { updatePermissionGroups, getAllPermissionGroups } from '../../actions/PermissionGroups';
import remove from '../../Res/Icon/permissionGroupsDelete.svg';
import './permissions.css';
import {
  PERMISSIONS,
  RESTRICTED_PERMISSION_GROUPS,
  PERMISSION_GROUPS,
} from '../../utils/constants';
import loadingSpinner from '../../Res/logo-loading.gif';
import { toast } from 'react-toastify';

const { Title } = Typography;
const styles = { loadingSpinner: { height: '640px', display: 'flex', alignItems: 'center', justifyContent: 'center' } };

const PermissionsUI = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.AppState.user);
  const token = localStorage.getItem('jwtToken') || null;

  const [permissionGroups, setPermissionGroups] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [originalState, setOriginalState] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [isDuplicateGroup, setIsDuplicateGroup] = useState(false);

  const groupedParents = useMemo(() => {
    const keys = Object.keys(PERMISSION_GROUPS || {});
    return keys.filter(k => Object.prototype.hasOwnProperty.call(PERMISSIONS, k));
  }, []);

  const parentOfChild = useMemo(() => {
    const map = {};
    Object.entries(PERMISSION_GROUPS || {}).forEach(([parentKey, children]) => {
      (children || []).forEach(childKey => {
        map[childKey] = parentKey;
      });
    });
    return map;
  }, []);

  const groupedChildrenSet = useMemo(() => {
    const s = new Set();
    groupedParents.forEach(parentKey => {
      const kids = PERMISSION_GROUPS[parentKey] || [];
      kids.forEach(k => s.add(k));
    });
    return s;
  }, [groupedParents]);

  const otherPermissions = useMemo(() => {
    return permissions.filter(p => !groupedParents.includes(p) && !groupedChildrenSet.has(p));
  }, [permissions, groupedParents, groupedChildrenSet]);

  useEffect(() => {
    const fetchPermissionGroups = async () => {
      try {
        //fetch all permission groups from backend
        const allPermissionGroups = await dispatch(getAllPermissionGroups(token))

        if (allPermissionGroups && Object.keys(allPermissionGroups).length > 0) {
          const data = allPermissionGroups;
          const groups = Object.keys(data);

          // Ensure newGroups are included with default false read/write
          const initialPermissions = groups.reduce((acc, group) => {
            const groupPerms = data[group] || {};
            acc[group] = Object.keys(PERMISSIONS).reduce((obj, perm) => {
              obj[perm] = {
                read: groupPerms[perm]?.read || false,
                write: groupPerms[perm]?.write || false,
              };
              return obj;
            }, {});
            return acc;
          }, {});
          setPermissionGroups(groups);
          // sort permissions alphabatically
          const sortedKeys = Object.entries(PERMISSIONS)
            .sort((a, b) => {
              const aLabel = String(a[1] || '');
              const bLabel = String(b[1] || '');
              if (aLabel < bLabel) return -1;
              if (aLabel > bLabel) return 1;
              return 0;
            })
            .map(([key]) => key);
          setPermissions(sortedKeys);
          // setPermissions(Object.keys(PERMISSIONS));
          setSelectedPermissions(initialPermissions);
          setOriginalState({ permissionGroups: groups, selectedPermissions: initialPermissions });
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching permission groups:", error);
        setIsLoading(false);
      }
    };

    if (user?.epikEngineering != undefined && user?.epikEngineering == true) {
      fetchPermissionGroups();
    }
  }, [user, dispatch, token]);

  useEffect(() => {
    if (user?.epikEngineering !== undefined && user?.epikEngineering !== true) {
      setIsLoading(false);
      toast.error(`You don't have permission to access Permission Groups`);
    }
  }, [user]);

  const detectChanges = (updatedGroups, updatedPermissions) =>
    JSON.stringify(updatedGroups) !== JSON.stringify(originalState.permissionGroups) ||
    JSON.stringify(updatedPermissions) !== JSON.stringify(originalState.selectedPermissions);
  const updateSelected = next => {
    setSelectedPermissions(next);
    setHasChanges(detectChanges(permissionGroups, next));
  };

  const handleCheckboxChange = (group, permission, type) => {
    const next = {
      ...selectedPermissions,
      [group]: {
        ...selectedPermissions[group],
        [permission]: {
          ...selectedPermissions[group]?.[permission],
          [type]: !selectedPermissions[group]?.[permission]?.[type],
        },
      },
    };
    updateSelected(next);
  };

  const handleParentToggle = (group, parentKey, type) => {
    const children = PERMISSION_GROUPS[parentKey] || [];
    const currentParent = selectedPermissions[group]?.[parentKey] || { read: false, write: false };
    const nextValue = !currentParent[type];

    const next = {
      ...selectedPermissions,
      [group]: {
        ...selectedPermissions[group],
        [parentKey]: {
          ...currentParent,
          [type]: nextValue,
        },
      },
    };
    if (type === 'read' && nextValue === false) {
      next[group][parentKey].write = false;

      children.forEach(child => {
        next[group][child] = { read: false, write: false };
      });

      updateSelected(next);
      return;
    }
    if (type === 'read' && nextValue === true) {
      children.forEach(child => {
        const prevChild = next[group][child] || { read: false, write: false };
        next[group][child] = { ...prevChild, read: true };
      });

      updateSelected(next);
      return;
    }

    // If turning WRITE off: clear children write only
    if (type === 'write' && nextValue === false) {
      children.forEach(child => {
        const prevChild = next[group][child] || { read: false, write: false };
        next[group][child] = { ...prevChild, write: false };
      });

      updateSelected(next);
      return;
    }

    // If turning WRITE on: ensure READ is on too, and set all children WRITE =istrue
    if (type === 'write' && nextValue === true) {
      next[group][parentKey].read = true;

      children.forEach(child => {
        const prevChild = next[group][child] || { read: false, write: false };
        next[group][child] = { ...prevChild, read: true, write: true };
      });

      updateSelected(next);
      return;
    }

    updateSelected(next);
  };

  const getGroupColorClass = index => `color-${index % 6}`;

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await dispatch(updatePermissionGroups(token, selectedPermissions));
      setOriginalState({ permissionGroups, selectedPermissions });
      setHasChanges(false);
    } catch (error) {
      console.error('Error updating permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveGroup = (group) => {
    const updatedGroups = permissionGroups.filter((g) => g !== group);
    const updatedPermissions = { ...selectedPermissions };
    delete updatedPermissions[group];

    setPermissionGroups(updatedGroups);
    setSelectedPermissions(updatedPermissions);
    setHasChanges(detectChanges(updatedGroups, updatedPermissions));
  };

  const handleAddPermissionGroup = () => {
    if (newGroupName && !isDuplicateGroup) {
      const updatedGroups = [...permissionGroups, newGroupName];
      const updatedPermissions = {
        ...selectedPermissions,
        [newGroupName]: permissions.reduce((acc, perm) => {
          acc[perm] = { read: false, write: false };
          return acc;
        }, {}),
      };

      setPermissionGroups(updatedGroups);
      setSelectedPermissions(updatedPermissions);
      setNewGroupName('');
      setIsGroupModalOpen(false);
      setHasChanges(detectChanges(updatedGroups, updatedPermissions));
    }
  };

  const handleGroupNameChange = (value) => {
    const lowerCaseGroups = permissionGroups.map(group => group.toLowerCase());
    setIsDuplicateGroup(lowerCaseGroups.includes(value.toLowerCase()));
    setNewGroupName(value);
  };

  if (isLoading) {
    return (
      <div style={styles.loadingSpinner}>
        <img src={loadingSpinner} alt="loading" className="pb-4" height={150} />
      </div>
    );
  }
  const renderPermissionRow = (permission, isSubRow = false) => (
    <Row key={permission} style={{ display: 'flex', flexWrap: 'nowrap' }}>
      <Col span={4} className={`permission-text ${isSubRow ? 'sub' : ''}`}>
        <Tooltip title={PERMISSIONS[permission]} placement="topLeft">
          {PERMISSIONS[permission]}
        </Tooltip>
      </Col>

      {permissionGroups.map((group, index) => {
        const parentKey = isSubRow ? parentOfChild[permission] : null;
        const parentReadOn = parentKey ? !!selectedPermissions[group]?.[parentKey]?.read : true;
        const parentWriteOn = parentKey ? !!selectedPermissions[group]?.[parentKey]?.write : true;

        return (
          <Col
            key={`${group}-${permission}`}
            className={`checkbox-column ${getGroupColorClass(index)}`}
            style={{ minWidth: '121px' }}
          >
            <Row justify="center" align="middle">
              <Col span={12}>
                <Checkbox
                  className={`${getGroupColorClass(index)}`}
                  disabled={isSubRow && !parentReadOn}
                  checked={!!selectedPermissions[group]?.[permission]?.read}
                  onChange={() => handleCheckboxChange(group, permission, 'read')}
                />
              </Col>
              <Col span={12}>
                <Checkbox
                  className={`${getGroupColorClass(index)}`}
                  disabled={isSubRow && !parentWriteOn}
                  checked={!!selectedPermissions[group]?.[permission]?.write}
                  onChange={() => handleCheckboxChange(group, permission, 'write')}
                />
              </Col>
            </Row>
          </Col>
        );
      })}
    </Row>
  );

  const renderParentRow = parentKey => (
    <Row key={parentKey} style={{ display: 'flex', flexWrap: 'nowrap' }}>
      <Col span={4} className="permission-text parent">
        <Tooltip title={PERMISSIONS[parentKey]} placement="topLeft">
          {PERMISSIONS[parentKey]}
        </Tooltip>
      </Col>

      {permissionGroups.map((group, index) => (
        <Col
          key={`${group}-${parentKey}`}
          className={`checkbox-column ${getGroupColorClass(index)}`}
          style={{ minWidth: '121px' }}
        >
          <Row justify="center" align="middle">
            <Col span={12}>
              <Checkbox
                className={`${getGroupColorClass(index)}`}
                checked={!!selectedPermissions[group]?.[parentKey]?.read}
                onChange={() => handleParentToggle(group, parentKey, 'read')}
              />
            </Col>
            <Col span={12}>
              <Checkbox
                className={`${getGroupColorClass(index)}`}
                checked={!!selectedPermissions[group]?.[parentKey]?.write}
                onChange={() => handleParentToggle(group, parentKey, 'write')}
              />
            </Col>
          </Row>
        </Col>
      ))}
    </Row>
  );

  return (
    <div className="permissions-container">
      {permissions.length > 0 && (
        <Row justify="end">
          {hasChanges && (
            <Button
              type="primary"
              className="permissions-update-btn"
              onClick={handleUpdate}
              disabled={loading}
            >
              {loading ? (
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 16, color: 'white' }} spin />}
                  size="small"
                />
              ) : (
                'Update'
              )}
            </Button>
          )}
        </Row>
      )}

      <Row>
        <Col span={24} flex="auto">
          <div style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
            <Row style={{ display: 'flex', flexWrap: 'nowrap' }}>
              <Col span={4} className="permissions-column">
                {permissions.length > 0 && (
                  <Row className="permissions-title-row">
                    <Col span={24}>
                      <Title className="permissions-title" level={4}>
                        Permissions
                      </Title>
                    </Col>
                  </Row>
                )}
              </Col>
              {permissionGroups.map((group, index) => (
                <Col key={group} className="group-column-header">
                  <Row
                    className={`group-column-title-wrapper ${getGroupColorClass(index)}`}
                  >
                    <Tooltip title={group} placement="topLeft">
                      <Title
                        level={5}
                        className="group-column-title"
                        style={{
                          maxWidth: !RESTRICTED_PERMISSION_GROUPS.includes(group) ? 'calc(100% - 30px)' : '100%'
                        }}
                      >
                        {group}
                      </Title>
                    </Tooltip>

                    {!RESTRICTED_PERMISSION_GROUPS.includes(group) && (
                      <Tooltip title="Remove Permission Group" placement="topLeft">
                        <span
                          onClick={() => handleRemoveGroup(group)}
                        >
                          <img src={remove} className="remove-icon" />
                        </span>
                      </Tooltip>
                    )}
                  </Row>

                  <Row className={'group-column-read-write-checkbox'}>
                    <Col span={12} className="label-column">Read</Col>
                    <Col span={12} className="label-column">Write</Col>
                  </Row>
                </Col>
              ))}
            </Row>

            {/* ===== GROUPED PERMISSIONS (PARENTS + CHILDREN) ===== */}
            {groupedParents.map(parentKey => (
              <React.Fragment key={`grp-${parentKey}`}>
                {permissions.includes(parentKey) && renderParentRow(parentKey)}
                {(PERMISSION_GROUPS[parentKey] || [])
                  .filter(child => permissions.includes(child))
                  .map(child => renderPermissionRow(child, true))}
              </React.Fragment>
            ))}

            {/* ===== ALL OTHER PERMISSIONS (UNCHANGED) ===== */}
            {otherPermissions.map(p => renderPermissionRow(p, false))}
          </div>
        </Col>
      </Row>
      <Modal
        title="Add Permission Group"
        visible={isGroupModalOpen}
        onOk={handleAddPermissionGroup}
        onCancel={() => {
          setNewGroupName('');
          setIsDuplicateGroup(false);
          setIsGroupModalOpen(false)

        }}
        okButtonProps={{ disabled: !newGroupName || isDuplicateGroup }}
      >
        <Input
          placeholder="Enter group name"
          value={newGroupName}
          onChange={(e) => handleGroupNameChange(e.target.value)}
        />
        {isDuplicateGroup && (
          <Typography.Text type="danger">
            This group name already exists. Please choose a unique name.
          </Typography.Text>
        )}
      </Modal>
    </div>
  );
};

export default PermissionsUI;